import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { request } from "hooks/useOkoApi/useOkoApi";
import { useAuthUserData } from "hooks/useAuthUserData/useAuthUserData";
import { buildPreviewChallenge } from "utils/challenge";
import { ConsensusActivityRender } from "../../Session/Activity/activities/ConsensusActivity/ConsensusActivityRender";
import { EvaluatorQuestionDetail } from "./EvaluatorQuestionDetail";
import { isEndpointMissing } from "./evaluatorApi";

// Standalone shareable view of one question. The id lives in the URL so the
// page can be linked directly. Full metadata (never blind) plus the same
// detail/review block as the two-pane view.
export const EvaluatorFullView = () => {
  const { id } = useParams();
  const { loading: authLoading } = useAuthUserData();
  const [challenge, setChallenge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const previewRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    if (!id) return undefined;
    setLoading(true);
    setError("");
    // Type-agnostic fetch: fetch_by_ids resolves MC or OR by id.
    request("questions/fetch_by_ids", {
      method: "POST",
      payload: { challenge_ids: [id] },
    })
      .then((res) => {
        if (cancelled) return;
        const item = Array.isArray(res) ? res[0] : res?.items?.[0];
        if (item) setChallenge(item);
        else setError("Question not found.");
      })
      .catch((e) => {
        if (cancelled) return;
        if (isEndpointMissing(e)) setError("Question not found.");
        else setError("Failed to load question.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  // The reused render path doesn't always trigger MathJax for inline LaTeX in
  // the prompt; typeset the preview once the question loads.
  useEffect(() => {
    const el = previewRef.current;
    const mj = typeof window !== "undefined" ? window.MathJax : null;
    if (!el || !mj || !challenge) return undefined;
    const timer = setTimeout(() => {
      try {
        if (mj.typesetPromise) mj.typesetPromise([el]).catch(() => {});
        else if (mj.typeset) mj.typeset([el]);
      } catch (e) {
        /* MathJax not ready; ignore */
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [challenge]);

  if (authLoading || loading) {
    return (
      <div style={{ padding: "48px", textAlign: "center" }}>
        <FontAwesomeIcon icon={faSpinner} spin size="3x" />
      </div>
    );
  }
  if (error) {
    return <div style={{ padding: "32px", color: "#a33" }}>{error}</div>;
  }

  // Full view: the question uses the full page width, with all metadata stacked
  // BELOW it (no left/right split).
  return (
    <div style={{ maxWidth: "1100px", margin: "0 auto", display: "flex", flexDirection: "column", gap: "20px" }}>
      <h4 style={{ marginBottom: 0 }}>Evaluator Review — Full View</h4>
      <div
        ref={previewRef}
        style={{
          width: "100%",
          border: "1px solid #eee",
          borderRadius: "8px",
          padding: "12px",
          background: "#fff",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <ConsensusActivityRender
          hideQuestion={false}
          challenge={buildPreviewChallenge(challenge)}
          onMCAnswer={() => {}}
          onORAnswer={() => {}}
          isMathJaxSet={false}
          showPromptFormula
        />
      </div>
      <EvaluatorQuestionDetail challenge={challenge} blindReview={false} />
    </div>
  );
};
